import React from 'react';
import { Field } from 'react-final-form';
import {
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';

import { RegistrationField } from '../../redux/types';

const StyledSelect = styled((props: Parameters<typeof Select>[0]) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Select classes={{ outlined: 'outlined' }} {...props} />
))`
  .outlined {
    padding: 10.5px 14px;
  }
`;

type ReactFinalFormCheckBoxType = Omit<RegistrationField, 'options' | 'conditional' | 'key'> & { keyValue: string };
export const ReactFinalFormCheckBox: React.FC<ReactFinalFormCheckBoxType> = ({
  label,
  keyValue,
  columnClassName,
  type = 'checkbox',
  defaultValue,
  disable,
  validate,
}: ReactFinalFormCheckBoxType) => (
  <div key={keyValue} className={columnClassName}>
    <Field
      name={keyValue}
      type={type}
      defaultValue={defaultValue}
      validate={validate}
    >
      {(fieldProps) => {
        const isError = !!(fieldProps.meta.modified && fieldProps.meta.error);
        return (
          <div className="pv2">
            <FormControl error={isError} disabled={disable}>
              <FormControlLabel
                control={(
                  <Checkbox
                    id={`FormCheckbox-${keyValue}`}
                    checked={fieldProps.input.checked}
                    onChange={fieldProps.input.onChange}
                    value={fieldProps.input.value}
                  />
                )}
                label={label}
              />
              {
                isError && (
                  <FormHelperText className="red">{fieldProps.meta.error}</FormHelperText>
                )
              }
            </FormControl>
          </div>
        );
      }}
    </Field>
  </div>
);

type ReactFinalFormSelectType = Omit<RegistrationField, 'key'> & { keyValue: string };

export const ReactFinalFormSelect: React.FC<ReactFinalFormSelectType> = ({
  label,
  keyValue,
  validate,
  columnClassName,
  options,
  disable,
}: ReactFinalFormSelectType) => (
  <div className={columnClassName} key={keyValue}>
    <Field name={keyValue} validate={validate}>
      {(fieldProps) => {
        const { meta, input } = fieldProps;
        const {
          error, submitFailed, pristine, modified,
        } = meta;
        const { name, value, onChange } = input;
        const isError = (!pristine || submitFailed || modified) && !!error;

        return (
          <div className="w-100 pa1 pv2">
            <FormControl variant="outlined" className="w-100" error={isError} required={!!validate} disabled={disable}>
              <InputLabel>{label}</InputLabel>
              <StyledSelect
                label={label}
                style={{ width: '100%' }}
                key={name}
                value={value}
                onChange={onChange}
              >
                <MenuItem key="none" value="">
                  <i><FormattedMessage id="none" /></i>
                </MenuItem>
                {
                  options?.map((option) => (
                    <MenuItem
                      key={`selectOption-${option.value}`}
                      value={option.value}
                    >
                      {option.viewValue}
                    </MenuItem>
                  ))
                }
              </StyledSelect>
              {isError && (
                <FormHelperText className="red">{error}</FormHelperText>
              )}
            </FormControl>
          </div>
        );
      }}
    </Field>
  </div>
);

type ReactFinalFormTextType = Omit<RegistrationField, 'key' | 'options'> & { keyValue: string };
export const ReactFinalFormText: React.FC<ReactFinalFormTextType> = ({
  label,
  keyValue,
  validate,
  columnClassName,
  disable,
  type,
  placeholder,
}: ReactFinalFormTextType) => (
  <div key={keyValue} className={columnClassName}>
    <Field
      name={keyValue}
      type={type}
      validate={validate}
    >
      {(fieldProps) => {
        const { meta, input } = fieldProps;
        const {
          submitFailed, pristine, error, modified,
        } = meta;
        const {
          name, type: inputType, value, onChange, onBlur,
        } = input;
        const showError = (!pristine || submitFailed || modified) && error;
        const helperText = (!pristine || submitFailed || modified) && error;
        return (
          <div className="w-100 pa1 pv2">
            <FormControl key={keyValue} className="w-100">
              <TextField
                required={!!validate}
                key={keyValue}
                id={`FormTextField-${name}`}
                label={label}
                placeholder={placeholder}
                onChange={onChange}
                onBlur={
                  onBlur as React.FocusEventHandler<
                    HTMLTextAreaElement | HTMLInputElement
                  >
                }
                fullWidth
                error={!!showError}
                helperText={helperText}
                type={inputType}
                value={value}
                disabled={disable}
                size="small"
                variant="outlined"
              />
            </FormControl>
          </div>
        );
      }}
    </Field>
  </div>
);
